/**
 * TutorialRenderer — screen-space overlay for the tutorial system.
 *
 * Displays: prompt text, input key hints, progress dots, success flash,
 * and the tutorial complete screen.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { WORLD, TUTORIAL } from '../../constants';
import { TUTORIAL_STEPS, DEFENSE_START_STEP, OFFENSE_STEP_COUNT } from '../../tutorial/steps';
import { COIN_AWARD } from '../../progression/catalog';
import type { GameState } from '../../types';

function monoStyle(size: number, color: number, bold = false): TextStyle {
  return new TextStyle({
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: size,
    fill: color,
    fontWeight: bold ? 'bold' : 'normal',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: WORLD.CANVAS_WIDTH * 0.7,
  });
}

export class TutorialRenderer {
  private container: Container;
  private promptText: Text;
  private subText: Text;
  private hintContainer: Container;
  private progressContainer: Container;
  private completeText: Text;
  private coinText: Text;
  private successFlash: Graphics;
  private backdrop: Graphics;

  // State tracking
  private currentStep = -1;
  private successFlashAlpha = 0;
  private completeAlpha = 0;
  private visible = false;

  constructor(uiContainer: Container) {
    this.container = new Container();
    this.container.zIndex = 500; // above HUD, below pause
    uiContainer.addChild(this.container);

    const cx = WORLD.CANVAS_WIDTH / 2;

    // Semi-transparent backdrop behind prompt text
    this.backdrop = new Graphics();
    this.container.addChild(this.backdrop);

    // Main prompt text
    this.promptText = new Text({
      text: '',
      style: monoStyle(TUTORIAL.PROMPT_FONT_SIZE, TUTORIAL.PROMPT_COLOR, true),
    });
    this.promptText.anchor.set(0.5, 0);
    this.promptText.position.set(cx, TUTORIAL.PROMPT_Y);
    this.container.addChild(this.promptText);

    // Sub text
    this.subText = new Text({
      text: '',
      style: monoStyle(TUTORIAL.SUB_TEXT_FONT_SIZE, TUTORIAL.SUB_TEXT_COLOR),
    });
    this.subText.anchor.set(0.5, 0);
    this.subText.position.set(cx, TUTORIAL.PROMPT_Y + 40);
    this.container.addChild(this.subText);

    // Input hint badges
    this.hintContainer = new Container();
    this.hintContainer.position.set(cx, TUTORIAL.PROMPT_Y + 80);
    this.container.addChild(this.hintContainer);

    // Progress dots
    this.progressContainer = new Container();
    this.progressContainer.position.set(cx, TUTORIAL.PROGRESS_Y);
    this.container.addChild(this.progressContainer);

    // Success flash overlay
    this.successFlash = new Graphics();
    this.container.addChild(this.successFlash);

    // Complete text
    this.completeText = new Text({
      text: 'TUTORIAL COMPLETE',
      style: monoStyle(48, TUTORIAL.SUCCESS_COLOR, true),
    });
    this.completeText.anchor.set(0.5);
    this.completeText.position.set(cx, WORLD.CANVAS_HEIGHT / 2);
    this.completeText.visible = false;
    this.container.addChild(this.completeText);

    // Coin reward text
    this.coinText = new Text({
      text: `+${COIN_AWARD.TUTORIAL} coins`,
      style: monoStyle(24, TUTORIAL.HINT_COLOR, true),
    });
    this.coinText.anchor.set(0.5);
    this.coinText.position.set(cx, WORLD.CANVAS_HEIGHT / 2 + 50);
    this.coinText.visible = false;
    this.container.addChild(this.coinText);

    this.container.visible = false;
  }

  /** Show the tutorial overlay. */
  show(): void {
    this.visible = true;
    this.container.visible = true;
    this.currentStep = -1;
  }

  /** Hide the tutorial overlay. */
  hide(): void {
    this.visible = false;
    this.container.visible = false;
    this.completeText.visible = false;
    this.coinText.visible = false;
    this.completeAlpha = 0;
    this.currentStep = -1;
  }

  /** Update each render frame. */
  update(state: GameState, dt: number): void {
    if (!this.visible || state.modeState.mode !== 'tutorial') {
      this.container.visible = false;
      return;
    }
    this.container.visible = true;

    const ms = state.modeState;
    const step = TUTORIAL_STEPS[ms.tutorialStep];
    const cx = WORLD.CANVAS_WIDTH / 2;

    // Step changed — update text
    if (ms.tutorialStep !== this.currentStep && step) {
      this.currentStep = ms.tutorialStep;
      this.promptText.text = step.promptText;
      this.subText.text = step.subText ?? '';
      this.subText.visible = !!step.subText;
      this.buildHintBadges(step.inputHints);
      this.buildProgressDots(ms.tutorialStep, ms.tutorialVariant);
    }

    // Update backdrop behind text
    this.backdrop.clear();
    const textBottom = step?.subText ? TUTORIAL.PROMPT_Y + 65 : TUTORIAL.PROMPT_Y + 38;
    const hintBottom = step?.inputHints.length ? textBottom + 40 : textBottom;
    this.backdrop.roundRect(cx - 450, TUTORIAL.PROMPT_Y - 15, 900, hintBottom - TUTORIAL.PROMPT_Y + 25, 8);
    this.backdrop.fill({ color: 0x000000, alpha: 0.6 });

    // Success flash (green pulse)
    if (ms.tutorialSuccessHold > 0) {
      this.successFlashAlpha = Math.min(1, this.successFlashAlpha + dt * 4);
    } else {
      this.successFlashAlpha = Math.max(0, this.successFlashAlpha - dt * 4);
    }
    this.successFlash.clear();
    if (this.successFlashAlpha > 0.01) {
      this.successFlash.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
      this.successFlash.fill({ color: TUTORIAL.SUCCESS_COLOR, alpha: this.successFlashAlpha * 0.08 });
    }

    // Tutorial complete screen
    if (ms.tutorialComplete) {
      this.completeAlpha = Math.min(1, this.completeAlpha + dt * 2);
      this.completeText.visible = true;
      this.completeText.alpha = this.completeAlpha;
      this.coinText.visible = true;
      this.coinText.alpha = this.completeAlpha;
      this.promptText.visible = false;
      this.subText.visible = false;
      this.hintContainer.visible = false;
      this.backdrop.visible = false;
    } else {
      this.completeText.visible = false;
      this.coinText.visible = false;
      this.completeAlpha = 0;
      this.promptText.visible = true;
      this.backdrop.visible = true;
    }

    // Hide during pause
    if (state.paused) {
      this.promptText.visible = false;
      this.subText.visible = false;
      this.hintContainer.visible = false;
      this.backdrop.visible = false;
      this.progressContainer.visible = false;
    } else {
      this.progressContainer.visible = !ms.tutorialComplete;
    }
  }

  /** Build input hint badges (key labels). */
  private buildHintBadges(hints: string[]): void {
    // Clear existing
    while (this.hintContainer.children.length > 0) {
      this.hintContainer.removeChildAt(0);
    }

    if (hints.length === 0) return;

    const badgeW = 80;
    const gap = 12;
    const totalW = hints.length * badgeW + (hints.length - 1) * gap;
    let x = -totalW / 2;

    for (const hint of hints) {
      const bg = new Graphics();
      bg.roundRect(x, -14, badgeW, 28, 6);
      bg.fill({ color: 0x000000, alpha: 0.5 });
      bg.stroke({ color: TUTORIAL.HINT_COLOR, width: 1.5, alpha: 0.8 });
      this.hintContainer.addChild(bg);

      const label = new Text({
        text: hint,
        style: monoStyle(13, TUTORIAL.HINT_COLOR, true),
      });
      label.anchor.set(0.5);
      label.position.set(x + badgeW / 2, 0);
      this.hintContainer.addChild(label);

      x += badgeW + gap;
    }
  }

  /** Build progress dots for the active variant's step range. */
  private buildProgressDots(currentStep: number, variant: 'offense' | 'defense'): void {
    while (this.progressContainer.children.length > 0) {
      this.progressContainer.removeChildAt(0);
    }

    const startStep = variant === 'defense' ? DEFENSE_START_STEP : 0;
    const total = variant === 'defense'
      ? TUTORIAL_STEPS.length - DEFENSE_START_STEP
      : OFFENSE_STEP_COUNT;
    const totalW = (total - 1) * TUTORIAL.PROGRESS_DOT_GAP;
    let x = -totalW / 2;

    for (let j = 0; j < total; j++) {
      const stepIdx = startStep + j;
      const dot = new Graphics();
      const r = TUTORIAL.PROGRESS_DOT_RADIUS;
      dot.circle(x, 0, stepIdx === currentStep ? r + 2 : r);

      if (stepIdx < currentStep) {
        dot.fill({ color: TUTORIAL.PROGRESS_DONE, alpha: 0.8 });
      } else if (stepIdx === currentStep) {
        dot.fill({ color: TUTORIAL.PROGRESS_ACTIVE, alpha: 1 });
      } else {
        dot.fill({ color: TUTORIAL.PROGRESS_PENDING, alpha: 0.5 });
      }
      this.progressContainer.addChild(dot);

      x += TUTORIAL.PROGRESS_DOT_GAP;
    }
  }

  destroy(): void {
    this.container.parent?.removeChild(this.container);
  }
}
